/**
 * The other names a nav destination goes by.
 *
 * What a page prints at its top is not here: that is its manifest entry's
 * label, `heading` and `description` (lib/navigation/nav-manifest.js), which
 * `components/nav/PageHeading.jsx` renders, so search matches the words the
 * page actually shows. This file holds the words no page prints: renames and
 * the shorthand a page is known by, so old vocabulary keeps working after a
 * rename ("FAQ" for Q&As, "FBIV" for Capability Maps).
 *
 * `aliases` are alternate names for a page, not keyword lists for semantic
 * detection (CLAUDE.md). `navigationPageIndex.test.js` asserts every key still
 * resolves to a live route.
 *
 * See docs/navigation-search.md for what the guards cannot catch (chiefly
 * missing aliases, which only real search traffic reveals).
 */

import { isOpaqueIdSegment } from "@/lib/navigation/page-search-match";

/**
 * Collapse a live href into a stable directory key: drop the locale prefix,
 * query string, and trailing slash, and replace the workspace id with a
 * placeholder so per-studio URLs share one entry.
 *
 * The workspace segment is recognised by the shape of an id, not by listing the
 * sibling routes it isn't. A denylist of static segments (`admin`, `reports`, …)
 * would go quietly wrong the day someone adds `/workspaces/settings`: that URL
 * would normalise to a studio-id key and lose its directory entry with no
 * failing test to show for it.
 */
export function normalizeRouteKey(href) {
  if (typeof href !== "string" || !href.trim()) return "";

  const [pathWithHash] = href.split("?", 1);
  const [path, hash = ""] = pathWithHash.split("#", 2);

  const normalizedPath = path
    .replace(/^\/(es|fr-CA)(?=\/|$)/, "")
    .replace(/^\/workspaces\/([^/]+)/, (match, segment) =>
      isOpaqueIdSegment(segment) ? "/workspaces/:workspaceId" : match,
    )
    .replace(/\/+$/, "")
    .toLowerCase();

  return hash ? `${normalizedPath}#${hash.toLowerCase()}` : normalizedPath;
}

const PAGE_DIRECTORY = {
  // ---------------------------------------------------------------- Learn
  "/playbook": {
    aliases: ["company overview", "positioning", "glossary", "lessons"],
  },
  "/playbook#our-content-faq": { aliases: ["faq", "faqs", "questions"] },
  "/playbook#fbiv": { aliases: ["fbiv", "features benefits impact value"] },
  "/playbook#messaging": { aliases: ["pitch", "pitches", "value message"] },
  "/playbook#cold-call": { aliases: ["cold call", "outbound", "inbound"] },
  "/dashboard/learning-paths": {
    aliases: ["lp", "courses", "curriculum"],
  },
  "/training": {
    aliases: ["training home", "to do", "assigned work"],
  },

  // ------------------------------------------------------------- Practice
  "/simulator": {
    aliases: ["simulator", "roleplay", "scenarios", "practice scenarios"],
  },
  "/simulator/personas": {
    aliases: ["buyer personas", "customer personas"],
  },
  "/simulator/seller-personas": {
    aliases: ["seller personas", "rep personas"],
  },
  "/simulator/on-the-fly": {
    // The simulator's top bar names it "On-the-Fly Practice".
    aliases: ["on-the-fly practice", "ad hoc practice", "quick practice"],
  },

  // ------------------------------------------------------------ Execution
  "/dem-cog/reports": {
    aliases: ["deal clinic", "cog", "cog report", "deal review"],
  },
  "/dem-cog/dems": {
    aliases: ["dem", "dems", "expert models"],
  },
  "/dashboard/transcript-results": {
    aliases: [
      "conversations",
      "conversation analyzer",
      "transcript analysis",
      "call analysis",
      "analyze a call",
      "recorded calls",
    ],
  },
  "/guide": {
    aliases: [
      "live coach",
      "live call coaching",
      "live feedback",
      "call coach",
      "real-time coaching",
      "guide - live call coach",
    ],
  },
  "/workspaces/scripts": {
    // Was "Email & Talking Script Generator" in the menu.
    aliases: ["email scripts", "talking scripts", "email & talking script generator"],
  },

  // ------------------------------------------------------------- Results
  "/dashboard": {
    aliases: ["my scores", "my progress", "home dashboard"],
  },
  "/leaderboard": { aliases: ["rankings", "standings"] },
  "/dashboard/assignments": {
    aliases: ["my assignments", "assigned practice", "pending work"],
  },
  "/dashboard/tests": {
    aliases: ["quizzes", "knowledge checks", "assessments"],
  },
  "/dashboard/okrs": {
    aliases: ["objectives", "key results", "goals"],
  },
  "/dashboard/badges": { aliases: ["achievements"] },
  "/dashboard/feedback-analysis": {
    aliases: ["my coaching", "ai coaching"],
  },
  "/dashboard/feedback-comments": {
    aliases: ["comments", "my feedback"],
  },
  "/dashboard/persona-chats": {
    aliases: ["chat history", "persona conversations"],
  },
  // Not "conversations": that is Conversation Analysis' own heading.
  "/chats": { aliases: ["sabine chats", "past chats", "chats with sabine"] },

  // ------------------------------------------------------------- Reports
  "/workspaces/reports": {
    // The menu was "Team Reports" until October 2026.
    aliases: ["all reports", "team reports", "team results", "reporting"],
  },
  "/workspaces/reports/manager-dashboard": {
    aliases: ["team overview", "manager view"],
  },
  "/workspaces/reports/execution": {
    aliases: ["execution usage", "adoption"],
  },
  "/workspaces/reports/transcript-results": {
    aliases: ["transcripts", "call analysis"],
  },
  // One report with a tab per period; the menu opens it on the month, so the
  // other periods' names find it here.
  "/workspaces/reports/engagement/monthly": {
    aliases: [
      "by month",
      "monthly report",
      "monthly engagement",
      "by quarter",
      "quarterly report",
      "quarterly engagement",
      "by week",
      "weekly report",
      "weekly engagement",
      "custom range",
      "custom dates",
      "by date range",
    ],
  },
  "/workspaces/reports/enrollments": {
    aliases: [
      "lp enrollments",
      "enrolled learners",
      "time tracking",
      "time spent",
      "hours",
    ],
  },
  // The one-rep Coaching Report is a tab of a member's page now.
  "/workspaces/reports/members": {
    aliases: [
      "team members",
      "my team",
      "my reps",
      "direct reports",
      "rep coaching report",
      "per rep coaching",
    ],
  },
  "/workspaces/reports/overdue-items": {
    aliases: ["late work", "past due"],
  },
  "/workspaces/reports/tests/faq": {
    aliases: ["faq tests"],
  },
  "/workspaces/reports/recordings": {
    aliases: ["call recordings", "audio"],
  },
  "/workspaces/reports/feedback-comments": {
    aliases: ["feedback comments"],
  },
  "/workspaces/reports/practice-sessions-export": {
    aliases: ["export sessions", "csv export", "download sessions"],
  },

  // --------------------------------------------------------------- Admin
  "/workspaces/:workspaceid/edit": {
    aliases: [
      "studio settings",
      "workspace settings",
      "configure studio",
      "studio profile",
      "notifications",
      "sales language",
      "connections",
      "integrations",
      "training modules",
      "plugins",
    ],
  },
  "/workspaces/:workspaceid/positioning": {
    aliases: ["value proposition", "messaging foundation"],
  },
  "/workspaces/admin/data/content": {
    aliases: ["content", "resources", "documents", "decks", "uploads"],
  },
  // The per-group pages under /glossary/groups/[groupId] cannot have their own
  // entries: a directory key has to resolve to a live nav destination (see
  // navigationPageIndex.test.js) and a group page's URL is only known once a
  // group exists. Their vocabulary rides along here instead, on the library
  // that lists them.
  "/workspaces/admin/glossary": {
    aliases: ["terms", "definitions", "glossary groups", "term groups"],
  },
  "/workspaces/admin/faqs": {
    aliases: ["faq", "faqs", "questions and answers"],
  },
  "/workspaces/admin/fbiv": {
    aliases: ["fbiv", "features benefits impact value"],
  },
  "/workspaces/admin/takeaway-reports": {
    aliases: ["key insights", "takeaways"],
  },
  "/workspaces/admin/people": {
    aliases: ["studio members", "users", "team members", "seats", "invite"],
  },
  "/workspaces/admin/people/communities": {
    aliases: ["groups", "teams"],
  },
  "/workspaces/admin/role-plays": {
    aliases: ["roleplays", "assign practice"],
  },
  "/workspaces/admin/learning-paths": {
    aliases: ["lp builder", "build a course"],
  },
  "/workspaces/admin/tests": {
    aliases: ["quizzes", "knowledge checks"],
  },
  "/workspaces/admin/faq-tests": {
    aliases: ["faq tests", "faq quizzes"],
  },
  "/workspaces/admin/glossary-tests": {
    aliases: ["term tests", "vocabulary quizzes"],
  },
  "/workspaces/admin/fbiv-tests": {
    aliases: ["fbiv tests"],
  },
  "/workspaces/admin/okrs": {
    aliases: ["goals"],
  },
  "/workspaces/admin/analytics": {
    // Was Admin › Insights › Analytics.
    aliases: ["analytics", "usage", "activity breakdown"],
  },
  "/workspaces/admin/data/modules": {
    aliases: ["training modules", "enable module", "module configuration"],
  },
  "/workspaces/admin/data/stories": {
    aliases: ["story prompts", "story builder", "story settings"],
  },
  "/workspaces/admin/data/rubrics": {
    aliases: ["studio rubrics", "scorecards", "grading", "scoring"],
  },
  "/workspaces/admin/data/discovery": {
    // These scenario headers use the studio's module name at runtime. Keep
    // their former names searchable without claiming a fixed on-page heading.
    aliases: ["Discovery", "discovery tracks"],
  },
  "/workspaces/admin/data/pitch": {
    aliases: ["Messaging / pitches", "pitches", "messaging"],
  },
  "/workspaces/admin/data/objections": {
    aliases: ["Objection handling", "customer objections"],
  },
  // Headed "Outbound & Inbound" whatever the studio calls the module, so the
  // default name and its old plural stay searchable in a studio that renamed it.
  "/workspaces/admin/data/cold-call": {
    aliases: ["outbound & inbound", "outbound & inbounds"],
  },
  "/workspaces/admin/data/plays": {
    aliases: ["Play scenarios", "sales plays"],
  },
  "/workspaces/admin/phone-practice": {
    aliases: ["phone role play", "call practice", "vapi"],
  },
  "/workspaces/admin/data/seller-personas": {
    aliases: ["seller personas", "rep personas"],
  },
  "/workspaces/broadcast": {
    aliases: ["announcement", "message everyone", "email the team"],
  },
  "/help/admin": {
    aliases: ["documentation", "how to", "admin help center"],
  },

  // ------------------------------------------------------------- Off-menu
  "/settings": {
    aliases: [
      "my profile",
      "password",
      "timezone",
      "email notifications",
      "two factor",
    ],
  },
  "/help": {
    aliases: ["support", "documentation", "contact us"],
  },
  "/help/member": {
    aliases: ["how to", "user guide", "learner help"],
  },
  // Headed from its article's content.json rather than the manifest.
  "/help/member/practice-sessions": {
    aliases: [
      "how does a practice session work",
      "practice mode",
      "live mic mode",
      "scored mode",
      "role-play instructions",
    ],
  },
  // Headed from its article's content.json rather than the manifest.
  "/help/admin/ai-assistants": {
    aliases: [
      "salesably in claude",
      "salesably in chatgpt",
      "codex",
      "ai assistant",
      "connector",
      "mcp",
    ],
  },
};

export function getPageDirectoryEntry(href) {
  return PAGE_DIRECTORY[normalizeRouteKey(href)] || null;
}

/** Every route this directory describes — used to test for stale entries. */
export function getPageDirectoryKeys() {
  return Object.keys(PAGE_DIRECTORY);
}
